import { createStore } from '@nile/mangifera/src/StoreManager/store/createStore';

// Define interface for the editor store state
interface EditorState {
  template: any | null;
}

// Define interface for cache configuration
interface CacheConfig {
  scope?: 'global' | 'local';
  expiryTime?: number;
  strategy?: string;
  tabBehavior?: string;
  clearOnRefresh?: boolean;
}

// Define interface for the store configuration
interface EditorStoreConfig {
  id: string;
  initialState: EditorState;
  methods: (
    set: (state: Partial<EditorState>) => void,
    get: () => EditorState
  ) => {
    setTemplate: (updatedTemplate: any) => void;
  };
  cache: {
    expiryTime: number;
    strategy: string;
    tabBehavior: string;
    clearOnRefresh: boolean;
    scope?: string;
  };
}

// Define interface for editor props
interface EditorProps {
  name?: string;
  id?: string;
  cacheConfig?: CacheConfig;
}

const editorStoreConfig: EditorStoreConfig = {
  id: 'editorStore',
  initialState: {
    template: null
  },
  methods: (set, get) => ({
    setTemplate: (updatedTemplate: any) => {
      set({ template: updatedTemplate });
    }
  }),
  cache: {
    expiryTime: 300,
    strategy: 'memory',
    tabBehavior: 'persist',
    clearOnRefresh: true
  }
};

// Store instance cache to prevent creating multiple instances for the same component
const storeInstances: Map<string, ReturnType<typeof createStore>> = new Map();

// Function to create or get existing store instance
const createEditorStoreInstance = (
  componentId: string,
  cacheConfig?: CacheConfig
) => {
  const isGlobal = cacheConfig?.scope === 'global';
  const cacheKey = `_SUPER_EDITOR_${componentId}_${
    isGlobal ? 'global' : 'regular'
  }`;

  if (storeInstances.has(cacheKey)) {
    return storeInstances.get(cacheKey)!;
  }

  const config: EditorStoreConfig = {
    ...editorStoreConfig,
    id: `editorStore_${componentId}`
  };

  if (cacheConfig?.scope === 'global') {
    config.cache.scope = 'global';
  }

  const storeInstance = createStore(config);
  storeInstances.set(cacheKey, storeInstance);
  return storeInstance;
};

export const useEditorStore = createStore(editorStoreConfig);

// Custom hook to return appropriate store based on props
export const useGlobalEditorStore = (props: EditorProps = {}) => {
  const { name = 'editor', id = 'default', cacheConfig } = props;

  // Create component id using ticket id with 'name' suffix
  const finalComponentId = `${id}_${name}`;

  // For regular components, create/get unique instance based on componentId
  if (finalComponentId) {
    return createEditorStoreInstance(finalComponentId, cacheConfig);
  }

  // Fallback to default store for backward compatibility
  return useEditorStore;
};
